// Inbox mirror of the add-on part of the admission rule (R7: AddOnBookingService.DecideAsync, called by
// RegistrationService on approval). The server stays authoritative and decides again when the registration is
// processed; these helpers only predict what the cards, the waitlist popup and the group dialog show.
//
// Order (R7): (1) event full → plain waitlist, no add-on check. (2) a requested add-on with "Ausgebucht →
// Warteliste" (waitlistWhenFull) does not fit → the whole person goes to the add-on waitlist ("Zusatz-Warteliste",
// mail slot equipment_waitlist, {Zusatz} = the exhausted names). (3) only when admitted: add-ons without waitlist
// are booked with what is left (min(requested, available), 0 = not booked) — a reduction, not a conflict.
// ForceConfirm skips (1) + (2) only; ForceAddOnWaitlist → add-on waitlist when the wish has a waitlist add-on.
import { addOnColorKey, available, formatCapacity } from '../../util/addOns';

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

// Sign-up add-ons of the registration's event with their usage (RegistrationResponse.eventAddOns:
// EventAddOnCapacity[]) → [{ addOnId, name, maxQuantity (0 = unlimited), used, waitlistWhenFull, colorKey }].
export function eventAddOnsOf(r) {
  return (Array.isArray(r?.eventAddOns) ? r.eventAddOns : [])
    .filter(a => a && a.addOnId != null)
    .map(a => ({
      addOnId: Number(a.addOnId),
      name: a.name ?? '',
      maxQuantity: toCount(a.maxQuantity),
      used: toCount(a.used),
      waitlistWhenFull: a.waitlistWhenFull === true,
      colorKey: addOnColorKey(a.colorKey),
    }));
}

// Requested add-ons of a registration (RegistrationAddOnInfo[]) → [{ addOnId, name, quantity ≥ 1, colorKey }].
export function requestedAddOnsOf(r) {
  return (Array.isArray(r?.addOns) ? r.addOns : [])
    .filter(a => a && a.addOnId != null && toCount(a.quantity) > 0)
    .map(a => ({
      addOnId: Number(a.addOnId),
      name: a.name ?? '',
      quantity: toCount(a.quantity),
      colorKey: addOnColorKey(a.colorKey),
    }));
}

// Used quantity per add-on (Map addOnId → used), the start value of a prediction.
export const usedByAddOn = (eventAddOns) => new Map(eventAddOns.map(a => [a.addOnId, a.used]));

const capacityOf = (a, used) => ({ addOnId: a.addOnId, name: a.name, used: used.get(a.addOnId) ?? 0, maxQuantity: a.maxQuantity });

/**
 * Prediction of one approval.
 * wish: requestedAddOnsOf(r); eventAddOns: eventAddOnsOf(r); used: Map addOnId → used (not modified).
 * options: eventFull (no free spot left), forceConfirm, forceAddOnWaitlist.
 * → { status: 'confirmed' | 'waitlist' | 'addOnWaitlist',
 *     waitlistAddOns: [{ addOnId, name, used, maxQuantity }]   (addOnWaitlist: the exhausted add-ons),
 *     reductions: [{ addOnId, name, requested, booked, available }] (confirmed: add-ons without waitlist cut down),
 *     booked: [{ addOnId, quantity }] (confirmed: what the participant gets) }
 * Requested add-ons the event's process does not offer (any more) are ignored, like the backend normalization.
 */
export function predictAdmission(wish, eventAddOns, used, { eventFull = false, forceConfirm = false, forceAddOnWaitlist = false } = {}) {
  const byId = new Map(eventAddOns.map(a => [a.addOnId, a]));
  const items = wish
    .map(w => ({ w, a: byId.get(w.addOnId) }))
    .filter(x => x.a);
  const free = (a) => available(a.maxQuantity, used.get(a.addOnId) ?? 0);
  const waitlistItems = items.filter(x => x.a.waitlistWhenFull);
  const exhausted = waitlistItems.filter(x => x.w.quantity > free(x.a));
  const result = (status, extra = {}) => ({ status, waitlistAddOns: [], reductions: [], booked: [], ...extra });

  if (forceAddOnWaitlist && waitlistItems.length > 0) {
    const reason = exhausted.length > 0 ? exhausted : waitlistItems;
    return result('addOnWaitlist', { waitlistAddOns: reason.map(x => capacityOf(x.a, used)) });
  }
  if (!forceConfirm) {
    if (eventFull) return result('waitlist');
    if (exhausted.length > 0) {
      return result('addOnWaitlist', { waitlistAddOns: exhausted.map(x => capacityOf(x.a, used)) });
    }
  }

  const reductions = [];
  const booked = [];
  items.forEach(({ w, a }) => {
    // Waitlist add-ons fit here (or ForceConfirm books them beyond the limit).
    if (a.waitlistWhenFull) {
      booked.push({ addOnId: a.addOnId, quantity: w.quantity });
      return;
    }
    const left = free(a);
    const quantity = Math.min(w.quantity, left);
    if (quantity < w.quantity) {
      reductions.push({ addOnId: a.addOnId, name: a.name, requested: w.quantity, booked: quantity, available: left });
    }
    if (quantity > 0) booked.push({ addOnId: a.addOnId, quantity });
  });
  return result('confirmed', { reductions, booked });
}

/**
 * Sammelanmeldung: members in registration order, cumulative — every admitted member takes its booked quantities
 * before the next one is checked (the backend evaluates them in the same order under the event lock).
 * members: [{ reg, status }] with status 'confirmed' | 'waitlist' | 'addOnWaitlist' (the dialog's choice);
 * explicit: the choice is sent as force flags (capacity or add-on conflict) — confirmed members then skip the
 * waitlist caps, otherwise the rule decides (a member may still land on the add-on waitlist).
 * → { results: Map regId → predictAdmission result, overbooked: [{ addOnId, name, by }] (waitlist add-ons booked
 *     beyond their limit by forced confirmations) }
 */
export function simulateGroup(members, eventAddOns, { explicit = false } = {}) {
  const used = usedByAddOn(eventAddOns);
  const results = new Map();
  members.forEach(({ reg, status }) => {
    const wish = requestedAddOnsOf(reg);
    let prediction;
    if (status === 'waitlist') {
      prediction = predictAdmission(wish, eventAddOns, used, { eventFull: true });
    } else if (status === 'addOnWaitlist') {
      prediction = predictAdmission(wish, eventAddOns, used, { forceAddOnWaitlist: true });
    } else {
      prediction = predictAdmission(wish, eventAddOns, used, { forceConfirm: explicit });
    }
    if (prediction.status === 'confirmed') {
      prediction.booked.forEach(b => used.set(b.addOnId, (used.get(b.addOnId) ?? 0) + b.quantity));
    }
    results.set(reg.id, prediction);
  });
  const overbooked = eventAddOns
    .filter(a => a.waitlistWhenFull && a.maxQuantity > 0)
    .map(a => {
      const before = Math.max(0, a.used - a.maxQuantity);
      const after = Math.max(0, (used.get(a.addOnId) ?? 0) - a.maxQuantity);
      return { addOnId: a.addOnId, name: a.name, by: after - before };
    })
    .filter(o => o.by > 0);
  return { results, overbooked };
}

/**
 * Waitlist add-ons the Sammelanmeldung asks for more of than is left — they decide the conflict, the banners and
 * the per-member option "Zusatz-Warteliste" (only for members who asked for one of them, otherwise the mail
 * would name a wrong reason).
 * → [{ addOnId, name, used, maxQuantity, available, persons, quantity }]
 */
export function groupAddOnShortages(regs, eventAddOns) {
  return eventAddOns
    .filter(a => a.waitlistWhenFull && a.maxQuantity > 0)
    .map(a => {
      const requests = regs
        .map(reg => requestedAddOnsOf(reg).find(w => w.addOnId === a.addOnId))
        .filter(Boolean);
      return {
        addOnId: a.addOnId,
        name: a.name,
        used: a.used,
        maxQuantity: a.maxQuantity,
        available: available(a.maxQuantity, a.used),
        persons: requests.length,
        quantity: requests.reduce((sum, w) => sum + w.quantity, 0),
      };
    })
    .filter(s => s.quantity > s.available);
}

// ── Texts ──────────────────────────────────────────────────────────────────────────────────────────────

// "A", "A und B", "A, B und C".
export function joinNames(names) {
  const list = (Array.isArray(names) ? names : []).filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} und ${list[list.length - 1]}`;
}

const withCapacity = (a) => (a.maxQuantity > 0 ? `${a.name} (${formatCapacity(a.used, a.maxQuantity)})` : a.name);

// Reason of an add-on waitlist: "Leihausrüstung ausgebucht (8/8)" / "Leihausrüstung (8/8) und Funk (4/4) ausgebucht".
export function waitlistAddOnText(waitlistAddOns) {
  const list = Array.isArray(waitlistAddOns) ? waitlistAddOns : [];
  if (list.length === 0) return '';
  if (list.length === 1) {
    const a = list[0];
    return a.maxQuantity > 0 ? `${a.name} ausgebucht (${formatCapacity(a.used, a.maxQuantity)})` : `${a.name} ausgebucht`;
  }
  return `${joinNames(list.map(withCapacity))} ausgebucht`;
}

// Predicted reduction (before approval): "BBs: nur noch 2 frei – wird auf 2 gekürzt" / "BBs: ausgebucht – wird nicht gebucht".
export function reductionNote(reduction) {
  return reduction.booked > 0
    ? `${reduction.name}: nur noch ${reduction.available} frei – wird auf ${reduction.booked} gekürzt`
    : `${reduction.name}: ausgebucht – wird nicht gebucht`;
}

// Reported reductions after processing (AddOnReduction[]): "BBs auf 2 gekürzt, Funk nicht gebucht" ('' without).
export function reducedAddOnsText(reductions) {
  const list = Array.isArray(reductions) ? reductions.filter(r => r && r.name) : [];
  return list
    .map(r => (r.booked > 0 ? `${r.name} auf ${r.booked} gekürzt` : `${r.name} nicht gebucht`))
    .join(', ');
}
